import React, {useState, useEffect, useRef, useCallback} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {MapContainer, TileLayer, useMap} from "react-leaflet";
import {InstructorMarkers} from "./InstructorMarkers";
import {MapViewController} from "./MapViewController";

/**
 * Inner controller that listens to wheel and touch events on the Leaflet map container
 * to provide cooperative gesture handling (Ctrl/Cmd + scroll to zoom, 2-finger touch to pan).
 */
export const MapGestureController = ({onShowPrompt}) => {
    const map = useMap();
    const touchStartRef = useRef({x: 0, y: 0});

    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();
        if (!container) return;

        // On touch-capable devices, ensure dragging is disabled by default for 1-finger touch
        if (typeof window !== "undefined" && "ontouchstart" in window) {
            if (map.dragging) map.dragging.disable();
        }

        const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
        const modifierKey = isMac ? "⌘" : "Ctrl";

        const handleWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                map.scrollWheelZoom.enable();
            } else {
                map.scrollWheelZoom.disable();
                onShowPrompt(`Bruk ${modifierKey} + rull for å zoome kartet`);
            }
        };

        const handleMouseDown = () => {
            // Enable dragging for mouse users on desktop
            if (map.dragging) map.dragging.enable();
        };

        const handleTouchStart = (e) => {
            if (e.touches && e.touches.length === 1) {
                touchStartRef.current = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                };
                if (map.dragging) map.dragging.disable();
            } else if (e.touches && e.touches.length >= 2) {
                if (map.dragging) map.dragging.enable();
            }
        };

        const handleTouchMove = (e) => {
            if (e.touches && e.touches.length === 1) {
                if (map.dragging) map.dragging.disable();
                const dx = e.touches[0].clientX - touchStartRef.current.x;
                const dy = e.touches[0].clientY - touchStartRef.current.y;

                // Only show guidance if user is trying to drag horizontally across the map
                // Vertical swipes are reserved for smooth page scrolling and must not trigger prompts
                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 20) {
                    onShowPrompt("Bruk to fingre for å flytte kartet");
                }
            } else if (e.touches && e.touches.length >= 2) {
                if (map.dragging) map.dragging.enable();
            }
        };

        const handleTouchEnd = (e) => {
            if (!e.touches || e.touches.length === 0) {
                if (map.dragging) map.dragging.disable();
            }
        };

        container.addEventListener("wheel", handleWheel, {passive: true});
        container.addEventListener("mousedown", handleMouseDown);
        container.addEventListener("touchstart", handleTouchStart, {passive: true});
        container.addEventListener("touchmove", handleTouchMove, {passive: true});
        container.addEventListener("touchend", handleTouchEnd, {passive: true});

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("mousedown", handleMouseDown);
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [map, onShowPrompt]);

    return null;
};

export const InstructorsMap = ({
    instructors,
    targetCoordinates = null,
    height = "100%",
    minHeight = 450,
    onExpressInterest,
}) => {
    const [promptMessage, setPromptMessage] = useState("");
    const [showPrompt, setShowPrompt] = useState(false);
    const timerRef = useRef(null);

    const handleShowPrompt = useCallback((message) => {
        setPromptMessage(message);
        setShowPrompt(true);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            setShowPrompt(false);
        }, 1400);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                height: height,
                minHeight: minHeight,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid #EAE6DF",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                position: "relative",
                "& .leaflet-container": {
                    width: "100% !important",
                    height: "100% !important",
                    maxWidth: "100% !important",
                    borderRadius: "inherit",
                    overflow: "hidden !important",
                    touchAction: "pan-y !important",
                },
                "& .leaflet-pane": {
                    touchAction: "pan-y !important",
                },
            }}
        >
            <MapContainer
                center={[65, 13]}
                zoom={5}
                scrollWheelZoom={false}
                style={{width: "100%", height: "100%"}}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapViewController targetCoordinates={targetCoordinates} zoom={12} />
                <InstructorMarkers instructors={instructors} onExpressInterest={onExpressInterest} />
                <MapGestureController onShowPrompt={handleShowPrompt} />
            </MapContainer>

            {/* Subtle bottom-floating guidance pill */}
            <Box
                aria-live="polite"
                data-testid="map-gesture-overlay"
                sx={{
                    position: "absolute",
                    bottom: 20,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1000,
                    pointerEvents: "none",
                    opacity: showPrompt ? 1 : 0,
                    transition: "opacity 0.25s ease-in-out",
                    width: "auto",
                    maxWidth: "88%",
                    textAlign: "center",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: {xs: "0.82rem", sm: "0.9rem"},
                        bgcolor: "rgba(23, 54, 35, 0.9)",
                        color: "#FFFFFF",
                        py: 0.8,
                        px: 2.2,
                        borderRadius: 20,
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                        backdropFilter: "blur(4px)",
                    }}
                >
                    {promptMessage}
                </Typography>
            </Box>
        </Box>
    );
};